import { createContext, useContext } from 'react'

/**
 * The prototype's "Prototype controls" (console.html, #demoPop), as dev-only state.
 *
 * `enabled` is false in every production build: it is `import.meta.env.DEV` AND
 * the switch in the controls. Sample data must never reach a customer, so a screen
 * reads its sample through the same gate -- see components/console/README.md.
 */

/** The six data states every console screen designs for. */
export type DataState = 'populated' | 'empty' | 'loading' | 'partial' | 'error' | 'offline'
/** The design's three viewers. Our membership roles map onto them (useConsoleRole). */
export type ConsoleRole = 'owner' | 'admin' | 'receptionist'
/** Overview's three layout directions (design docs §03). A is the default. */
export type OverviewLayout = 'A' | 'B' | 'C'

export interface DemoSettings {
  enabled: boolean
  dataState: DataState
  role: ConsoleRole
  overviewLayout: OverviewLayout
  agentPaused: boolean
}

export interface DemoValue extends DemoSettings {
  setEnabled: (enabled: boolean) => void
  setDataState: (state: DataState) => void
  setRole: (role: ConsoleRole) => void
  setOverviewLayout: (layout: OverviewLayout) => void
  setAgentPaused: (paused: boolean) => void
}

export const DEMO_DEFAULTS: DemoSettings = {
  enabled: false,
  dataState: 'populated',
  role: 'owner',
  overviewLayout: 'A',
  agentPaused: false,
}

const noop = () => {}

/** What every screen sees outside the console frame, in tests, and in production. */
export const DEMO_OFF: DemoValue = {
  ...DEMO_DEFAULTS,
  setEnabled: noop,
  setDataState: noop,
  setRole: noop,
  setOverviewLayout: noop,
  setAgentPaused: noop,
}

export const DemoContext = createContext<DemoValue>(DEMO_OFF)

/** The prototype controls' current settings. `enabled` is always false in production. */
export function useDemo(): DemoValue {
  return useContext(DemoContext)
}
